<template>    

  <ion-page id="lichessplay-page">
    <ion-content>

      <div v-if="showState == SHOW_EXPLAINTOUR">
        <ExplainTour :screen="explainTourScreen"
                      :wordsSpoken="explainTourWordsSpoken"
                      :wordsSpokenTrigger="explainTourWordsSpokenTrigger"
                    @toolbar-click="explainTourToolbarClick" />
      </div>

      <div v-if="showState == SHOW_SINGLE_OPTION">
            <SingleOption :displayArray="singleOptionArray"
                        :descriptionHeading="singleOptionHeading"
                        :initialSelected="singleOptionSelected"
                        :wordsSpoken="singleOptionWordsSpoken"
                        :wordsSpokenTrigger="singleOptionWordsSpokenTrigger"

                        @item-selected="singleOptionClick" 
                        @toolbar-click="singleOptionToolbarClick"/>
      </div>

      <div v-show="showState == SHOW_PLAY">
        <TopLine :style="styleTopline"  :title="playLichessTitleString"  :microphoneIsMuted="microphoneIsMuted" 
                          @topline-click="toplineClick"/>
        <div :style="styleMiddle">

          <div id="the-board" :style="styleBoard">
            <CMChessboard :fen="forceFen" :validMoves="availableMoves" 
                        :orientation="orientation" 
                        :boardSize="boardSize" 
                        :animateOnePly="animateFen"
                        @board-move="boardMove" />
          </div>
          <div  :style="styleTextArea">
            <div v-html="textareaMessage"></div>
            <div v-show="claimWinFailed">
              <b>Claim Win failed with error : {{ claimResponseError }}</b>
            </div>
            <div v-show="opponentIsGone">
              <b>Opponent is Gone</b>
              <div v-show="claimWinInSeconds == 0">
                <div v-show="availableMoves.length == 0">
                <b>{{ translatePhrase("You can Claim Win Now") }}</b>
                </div>
                <div v-show="availableMoves.length != 0">
                  <b>{{ translatePhrase("You can Claim Win - AFTER making move") }}</b>
                </div>
              </div>
              <div v-show="claimWinInSeconds > 0">
                <i>{{ translatePhrase("Claim Win in") }} {{ claimWinInSeconds }} {{ translateEnglishWords("seconds") }}</i>
              </div>
            </div>
          </div>
          <div  :style="styleOpponent">
            <div v-if="opponentNameDisplay == 'AI'" class="name-text blurred-text">Name Withheld</div>
            <div v-if="opponentNameDisplay != 'AI'" class="name-text">{{  opponentNameDisplay }}</div>
            <div class="time-text">{{ opponentTimeDisplay }}</div>      
          </div>
          <div :style="stylePlayer">
            <div class="name-text">{{ statusDisplay }}</div>
            <div class="time-text">{{ playerTimeDisplay }}</div>
          </div>
        </div>
        <ToolBar :style="styleToolbar" :elements="toolbarElements" :trigger="toolbarTrigger" @toolbar-click="toolbarClick" />
      </div>
    </ion-content>
  </ion-page>
  
</template>

<script setup>

  import { IonPage, IonContent  } from '@ionic/vue';
  import { computed } from "@vue/reactivity"

  import CMChessboard from '@/components/CMChessboard.vue'

  import { ref, onMounted, onBeforeUnmount, watch } from 'vue'

  import ExplainTour from '@/components/ExplainTour.vue'

  import Explain from '@/components/Explain.vue'
  import ToolBar from '@/components/ToolBar.vue'
  import TopLine from '@/components/TopLine.vue'

  import { Chess } from 'chess.js'

  import SingleOption from '@/components/SingleOption.vue'

  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '../../stores/windowSizeStore';

  import AsyncLichessStream from '@/components/AsyncLichessStream.vue'

  import { doStartupCheck } from '@/composables/startupCheck.js'

  import { buildWordList, parseLichessCommands, parseComputerCommands, parsePlay, 
                    parseGameWordsSaid , parseDescribeCommands, processDescribe}
                    from '@/composables/parsePlay.js'
  import { buildWordListFromChoiceList , matchValue, parseGeneralWordsSaid } from '@/composables/wordlistSupport.js'

  import { buildStyleToolbar, buildStyleTopline, availableWidthHeight, buildStyleMiddle,
              calculateChessBoardRect, isScreenPortraitOrientation } from '@/composables/screenSize'
  import { useGetData , generateErrorAlertText, getLichessData, doAxiosAbort, Utf8ArrayToStr } from '@/composables/getData.js'
  import { translateEnglishWords , translatePhrase} from '@/composables/translation';
  import { buildPieceLocationStrings, getPieceSetName , columnToNumber, 
        buildAvailableMoves, buildVerbalSan} from '@/composables/blindfoldSupport.js'

const emit = defineEmits(['where-next','word-list','mic-is-muted','string-to-speak'])

  const props = defineProps({
      wordsSaid : {
        type : String,
        default : ""
      },
      wordsSaidTrigger : {
        type : Number,
        default : 0
      }
    })

  const wordsSaid = computed({get() {return props.wordsSaid}})
  const wordsSaidTrigger = computed({get() {return props.wordsSaidTrigger}})

  const NO_DRAW_ON_TABLE = 1
  const PLAYER_OFFERED_DRAW = 2
  const OPPONENT_OFFERED_DRAW = 3

  const NO_GAME = 0
  const IN_GAME = 1
  const GAME_ENDED = 2

  var gameState = NO_GAME

  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const connectionStore = useConnectionStore()

  // once we play, don't start on landing page
  connectionStore.setStartOnLandingPage("no")
  
  var playType = connectionStore.getPlayType
  const fileWords = connectionStore.getFileWords
  const languageInUse = connectionStore.getLanguageInUse

  var lichessAccessToken = connectionStore.getLichessAccessToken
  var lichessURLGameStream = ""
  const lichessUserid = connectionStore.getLichessUserid

  var isDoneWithLichessStream = false
  var hasActiveLichessStream = false

  const opponentIsGone = ref(false)
  const claimWinInSeconds = ref(20)
  const claimWinFailed = ref(false)
  const claimResponseError = ref("")

  const playLichessTitleString = ref(translateEnglishWords("Play Lichess"))

  const lichessNoTimerValue = 2147483647
  var opponentSecondsLeft = 0
  var playerSecondsLeft = 0
  var playingColorString = "?"
  var opponentUsername = ""
  var gameTimerIsCountingDown = false
  var gameTimerAgainstPlayer = false
  var timerAmount = 0
  var timerCanStop = false
  var stopWaiting = false
  var drawStatus = NO_DRAW_ON_TABLE
  var gameIsOver = 0
  var playerInCheck = 0
  var opponentMoveToRepeat
  var processingGameFull = false

  var displayFen = INITIAL_FEN
  var pgnLast10 = ""
  var pgn = ""

  var opponentMoveCount = 0
  var opponentFen = INITIAL_FEN
  var opponentSan = ""
  var opponentVerbalSan = ""

  var playerMoveCount = 0

  const textareaMessage = ref("<p><b>" + translateEnglishWords("Loading Game") + "</b>")

  const gameIdWithWhoSent =  connectionStore.getLichessGameId
  const gameIdArray = gameIdWithWhoSent.split(",")
  const openGameId = gameIdArray[0]
  const whoSent = gameIdArray[1]

  //console.log("OPENGAME ID ",openGameId)
  var moveNumber = 0

  const isDark = ref(connectionStore.getDarkmode != "light")
  
  var didTakeBack = false

  const SHOW_PLAY = 0
  const SHOW_EXPLAINTOUR = 1
  const SHOW_SINGLE_OPTION = 2

  const showState = ref(SHOW_PLAY)

  const opponentNameDisplay = ref(translateEnglishWords("Opponent Name"))
  const opponentTimeDisplay = ref(translateEnglishWords("Opponent Time"))
  const playerTimeDisplay = ref(translateEnglishWords("Player Time"))
  const statusDisplay = ref("status")
  var waitingCounter = 0
  var explainForState = SHOW_PLAY

  const TOOLBAR_RESIGN = 0
  const TOOLBAR_EXPLAINTOUR = 1

  var toolbarElements = ref([
    { index : TOOLBAR_RESIGN, label : translateEnglishWords("Resign") ,disabled : false},
    { index : TOOLBAR_EXPLAINTOUR, label : translateEnglishWords("Explain") ,disabled : false},
  ])

  const toolbarTrigger = ref(0)

  const microphoneIsMuted = ref(connectionStore.getMicrophoneIsMuted)
  emit("mic-is-muted",microphoneIsMuted.value)

  const singleOptionWordsSpoken = ref("")
  const singleOptionWordsSpokenTrigger = ref(0)

  const explainTourWordsSpoken = ref("")
  const explainTourScreen = ref("")
  const explainTourWordsSpokenTrigger = ref(0)

  const wordlistPlay = buildWordList("lichess",languageInUse)
  const wordlistExplain = "command done scroll up down mute listen"
  const wordlistTrigger = ref(0)
  const wordlist = ref(wordlistPlay)
  emit("word-list",wordlistPlay)


  var scrollCounter = 0
  const scrollTrigger = ref(0)
  const explainScrollTrigger = ref(0)
     
  const TEXTAREA_FONTSIZE = 16

  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  const boardSize = ref(100)
  var blankAppendTrigger = ""

  const initialFen = ref(INITIAL_FEN)
  const forceFen = ref(INITIAL_FEN)
  const animateFen = ref([])
  const pieceSet = ref("regular")
  var moveStackChessJS = []

  var fenAfterLastEngineMove = ""

  var playingWhite = 1
  var orientation = ref("white")

  const availableMoves = ref([])
  var playerSans = []
  
  var chess = new Chess()


  const singleOptionHeading = ref("")
  const singleOptionArray = ref([])
  var singleOptionDisplayWordsArray = []
  var useSingleOptionDisplayWords = ""
  var singleOptionServerField = ""
  const singleOptionSelected = ref("")

  var firstAmbiguous = true

  var playKeyword = ""

  // string sent to lichess to report if there is an error
  var urlSentToLichess = ""
    
  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleTextArea = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "font-size" : TEXTAREA_FONTSIZE + "px",
    "text-align" : "center",
    "line-height" : "1.2em"
  })

  const styleOpponent = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const stylePlayer = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleToolbar = ref(buildStyleToolbar(availableWidthHeight()))
  const styleTopline = ref(buildStyleTopline(availableWidthHeight()))
  const styleMiddle = ref(buildStyleMiddle(availableWidthHeight()))

  var controller = null


  doStartupCheck(false,afterStartupCallbackRoutine)

  function afterStartupCallbackRoutine(stuffChanged) {
    //console.log("*********** open game id ",openGameId)

    lichessURLGameStream = "https://lichess.org/api/board/game/stream/" + openGameId
    setupFetch(lichessURLGameStream,lichessAccessToken)

    updateWidthHeights()
  }

   onMounted(() => {
    
  })

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"
    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleTextArea.value.height = boundaries['textArea'].height + "px"
    styleTextArea.value.width = boundaries['textArea'].width + "px"
    styleTextArea.value.top = boundaries['textArea'].top + "px"
    styleTextArea.value.left = boundaries['textArea'].left + "px"

    styleOpponent.value.height = boundaries['opponent'].height + "px"
    styleOpponent.value.width = boundaries['opponent'].width + "px"
    styleOpponent.value.top = boundaries['opponent'].top + "px"
    styleOpponent.value.left = boundaries['opponent'].left + "px"

    stylePlayer.value.height = boundaries['player'].height + "px"
    stylePlayer.value.width = boundaries['player'].width + "px"
    stylePlayer.value.top = boundaries['player'].top + "px"
    stylePlayer.value.left = boundaries['player'].left + "px"

    //console.log("**** boardsize ",boardSize.value)
  }

  function updateWidthHeights() {

    var availableSpace = availableWidthHeight()

    const isPortrait = isScreenPortraitOrientation(availableSpace)

    styleToolbar.value = buildStyleToolbar(availableWidthHeight())
    styleTopline.value = buildStyleTopline(availableWidthHeight())
    styleMiddle.value = buildStyleMiddle(availableWidthHeight())

    const boundaries = (isPortrait) ? 
                calculatePortraitWidthHeights(availableSpace) :  
                calculateLandscapeWidthHeights(availableSpace)
    
    updateStyleObjects(boundaries)
}

function calculatePortraitWidthHeights(availableSpace) {
  //console.log("available space ",availableSpace)

  // reserve space for the instruction lines
  const spaceForHeight = availableSpace.height - TEXTAREA_FONTSIZE * 7 * 1.2
  const availableSpaceForBoard = { width : availableSpace.width ,
              height : spaceForHeight- availableSpace.topIndent - 56,
              isPortrait : true }
  const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

  const opponentPositionTop = 0
  const opponentPositionLeft = availableSpace.leftRightIndent
  const opponentHeight = TEXTAREA_FONTSIZE + 6
  const opponentWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = opponentPositionTop + opponentHeight + boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const playerPositionTop = boardPositionTop + boardHeight
  const playerPositionLeft = availableSpace.leftRightIndent
  const playerHeight = TEXTAREA_FONTSIZE + 6
  const playerWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const textAreaPositionTop = playerPositionTop + playerHeight
  const textAreaPositionLeft = availableSpace.leftRightIndent
  const textAreaHeight = availableSpace.height - textAreaPositionTop
  const textAreaWidth = availableSpace.width - availableSpace.leftRightIndent * 2

  const result = { board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft ,
      top : boardPositionTop
    },
    textArea : {
      width : textAreaWidth,
      height : textAreaHeight,
      left : textAreaPositionLeft,
      top : textAreaPositionTop
    },
    opponent : {
      width : opponentWidth,
      height : opponentHeight,
      left : opponentPositionLeft,
      top : opponentPositionTop
    },
    player : {
      width : playerWidth,
      height : playerHeight,
      left : playerPositionLeft,
      top : playerPositionTop
    }
  }
  //console.log("board and movelist dimensions",result)
  return (result)
}

function calculateLandscapeWidthHeights(availableSpace) {

  const spaceForWidth = (showState.value == SHOW_PLAY) ? availableSpace.width - 150 :
                               availableSpace.width /2
  // reserve space for the instruction lines
  const availableSpaceForBoard = { width : spaceForWidth,
              height : availableSpace.height - availableSpace.topIndent - 56,
              isPortrait : false }
  const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

  const boardHeight = boardDimensions.height
  const boardWidth = boardDimensions.width
  const boardPositionTop = boardDimensions.y
  const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

  const opponentPositionTop = 0
  const opponentPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
  const opponentHeight = TEXTAREA_FONTSIZE + 6
  const opponentWidth = availableSpace.width - opponentPositionLeft

  const playerHeight = opponentHeight
  const playerPositionTop = availableSpace.height - playerHeight
  const playerPositionLeft = opponentPositionLeft
  const playerWidth = opponentWidth

  const textAreaPositionTop = opponentPositionTop + opponentHeight
  const textAreaPositionLeft = opponentPositionLeft
  const textAreaHeight = availableSpace.height - opponentHeight - playerHeight
  const textAreaWidth = opponentWidth

  const result = { board : {
      width : boardWidth,
      height : boardHeight,
      left : boardPositionLeft ,
      top : boardPositionTop
    },
    textArea : {
      width : textAreaWidth,
      height : textAreaHeight,
      left : textAreaPositionLeft ,
      top : textAreaPositionTop
    },
    opponent : {
      width : opponentWidth,
      height : opponentHeight,
      left : opponentPositionLeft,
      top : opponentPositionTop
    },
    player : {
      width : playerWidth,
      height : playerHeight,
      left : playerPositionLeft,
      top : playerPositionTop
    }
  }
  //console.log("board and movelist dimensions",result)
  return (result)
}

 watch(wordsSaidTrigger, (newVal,oldVal) => {
     //console.log('*********** words said ',props.wordsSaid)
     processWords(props.wordsSaid)
   })

  watch(() => windowSizeStore.getWindowSize, () => {
      //console.log('*********** Explorer -- window size changes detected')
      windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] }
      //console.log("*** Explorer new window size",windowSizeRef.value)
      updateWidthHeights()
    })

  // START ****************** MOVED FROM AsyncLichessStream

  function abortFunction(controller) {
    //console.log("******* IN Abort Function",controller)
    if (controller != null) {
      try {
        controller.abort()
      }
      catch (error) {
        console.log("abort error",error)
      }
    }
    controller = null
    //console.log("end abort function")
  }

  async function setupFetch (url,accessToken) {
    if (url == "" || accessToken == "")
      return

    //console.log("SETUP FETCH",url,accessToken)

    controller = new AbortController()

    var response = null
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept' : 'application/json',
          'Authorization' : "Bearer " + accessToken
        },
        signal : controller.signal
      })
    }
    catch(error) {
      console.error('Error:', error);
    }

    const reader = (response == null) ? null : response.body.getReader();

    // check if we're done with the stream on each loop. Calling program sets 'isDone'
    while (reader != null && controller != null && isDoneWithLichessStream == false) {
      const {value, done} = await reader.read();
      if (controller == null || done) 
        break;
      const receivedString = Utf8ArrayToStr(value)
      receivedEventString(receivedString)
      //console.log('Received', receivedString,value,isDoneWithLichessStream);
    }

    receivedEventString("ABORT")
    //console.log("before abort call")
    abortFunction(controller)
    //console.log('Response fully received');
   
  }

  // END ****************** MOVED FROM AsyncLichessStream

  function receivedEventString(string) {
    //console.log("***** GOT GAME STRING ->",string)

    // The event stream has to finish before we leave the screen
    if (string == "ABORT") {
      hasActiveLichessStream = false
      // timer will send them to calling View
      return
    }

    hasActiveLichessStream = true

    // getting all the stream traffic so we know it is alive
    if (string == "\n")
      return
    

    const entries = string.split("\n");
    //console.log("***** Game Entries",entries)
    if (entries.length == 0)
      return;
    for (var i=0; i < entries.length; i++) {
      if (entries[i].length == 0)
        continue
      const oneEntry = JSON.parse(entries[i])
      //console.log("*** entry ",i,oneEntry)
      if (oneEntry.hasOwnProperty('type')) {
        switch (oneEntry['type']) {
          case "gameFull" :
            processingGameFull = true
            parseGameFull(oneEntry)
            break
          case "gameState" :
            processingGameFull = false
            parseGameState(oneEntry)
            break
          case "chatLine" :
            break
          case "opponentGone" :
            if (oneEntry.hasOwnProperty('gone')) {
              const goneState = oneEntry['gone']
              // if gone state has changed, voice that
              if (goneState != opponentIsGone.value) {
                saySomething(true, (goneState) ? translatePhrase("Opponent Gone") :
                              translatePhrase("Opponent Returned"))
              }
              opponentIsGone.value = oneEntry['gone']
            }
            if (oneEntry.hasOwnProperty('claimWinInSeconds'))
              claimWinInSeconds.value = oneEntry["claimWinInSeconds"]

            toolbarElements.value[TOOLBAR_RESIGN].label = 
                    (opponentIsGone.value && claimWinInSeconds.value == 0) 
                    ? translateEnglishWords("Claim Win") 
                    : (canAbortGame() ? translateEnglishWords("Abort") 
                          : translateEnglishWords("Resign"))
            toolbarTrigger.value++
            break
        }
      }

    }
  }

  function parseGameFull(json) {
    const whiteJson = json['white']
    const blackJson = json['black']
    const stateJson = json['state']

		const whiteId = whiteJson.hasOwnProperty('id') ? whiteJson['id'] : "ai";
		const whiteName = whiteJson.hasOwnProperty('name') ? whiteJson['name'] : "AI";
    const blackId = blackJson.hasOwnProperty('id') ? blackJson['id'] : "ai";
		const blackName = blackJson.hasOwnProperty('name') ? blackJson['name'] : "AI";

    if (whiteId == lichessUserid) {
      playingColorString = "w"
      opponentUsername = blackName
    }
    else {
      playingColorString = "b"
      opponentUsername = whiteName
    }

    // display the opponent name
    opponentNameDisplay.value = opponentUsername
    
    // initial dispay on the board
    orientation.value = (playingColorString == "w") ? "white" : "black"

    // if from challenge verbalize game has started
    if (whoSent == "/lichesschallenge")
      saySomething(true,(playingColorString == "w") ? 
                  translatePhrase("Game Started, You Play White") :
                  translatePhrase("Game Started, You Play Black"))

    parseGameState(stateJson)
  
  }

  function parseGameOver(status,winner) {
    availableMoves.value = []
    gameTimerIsCountingDown = false

    saySomething(true,translateEnglishWords("Game Over"))
    const displayWinner = (winner != "") ? "  " + translateEnglishWords("Winner") + ":" + winner : ""
    statusDisplay.value = translateEnglishWords("Game Over") + ": " + status + displayWinner
  }

  function updateCurrentFenAfterDelay(currentFen) {

    let timerSleepAmount = 500

    function oneInterval() {
      clearInterval(id)
      forceFen.value = currentFen
  
    }
    var id = setInterval(oneInterval, timerSleepAmount)
  }

  function parseGameState(json) {

    const status = json.hasOwnProperty('status') ? json['status'] : ""
    const winner = json.hasOwnProperty('winner') ? json['winner'] : ""

    if (status == "created")
      return
  
    const moves = json['moves']
    const wtime = json['wtime']
    const btime = json['btime']

    const whiteDraw = json.hasOwnProperty('wdraw') ? json['wdraw'] : ""
    const blackDraw = json.hasOwnProperty('bdraw') ? json['wdraw'] : ""

    // https://github.com/lichess-org/scalachess/blob/master/src/main/scala/Status.scala

 
    const moveArray = (moves.length == 0) ? [] : moves.split(" ")
    const plyCount = moveArray.length
    const colorToMove = (plyCount % 2) ? "b" : "w"

    // build move stack since Lichess always sends us the UCI from initial fen
    chess.load(INITIAL_FEN)
    var lastMoveChessJS = null
    if (moves != "") {
      for (var i=0; i < moveArray.length; i++) {
        const moveResult = chess.move(moveArray[i])
        moveStackChessJS.push(moveResult)
        if (i == moveArray.length - 1) {
          lastMoveChessJS = moveResult
          //console.log("Last moveResult",moveResult)
        }
      }
    }

    // build display move string of last 10 moves. Always start on a white move
    var startingPly = (moveArray.length <= 21) ? 0 : moveArray.length  - 20
    if (startingPly % 2 == 1)
      startingPly--
    var pgnLast10 = ""
    moveNumber = 1
    for (var i=startingPly; i < moveArray.length; i++) {
      if (i % 2 == 0) {
        moveNumber = i / 2 + 1
        pgnLast10 += moveNumber + "." 
      }
      pgnLast10 += moveStackChessJS[i].san + " "
    }

    var firstMoveNote = ""
    if (moveNumber <= 1)
      firstMoveNote = buildfirstMoveNote()

    if (processingGameFull) {
      displayFen = (lastMoveChessJS == null) ? INITIAL_FEN : lastMoveChessJS.after
      forceFen.value = displayFen
      textareaMessage.value = pgnLast10 + "<br>" + firstMoveNote
      if (lastMoveChessJS != null) {
        const speakString = buildVerbalSan(lastMoveChessJS,fileWords)
        saySomething((colorToMove != playingColorString),speakString)
      }

    }
    else
      if (lastMoveChessJS != null) {
        const moveDots = (moveArray.length % 2 == 1) ? ". " : "... "
        textareaMessage.value = moveNumber + moveDots + lastMoveChessJS.san

        const speakString = buildVerbalSan(lastMoveChessJS,fileWords)
        //console.log("SPEAK STRING",speakString)
        if (colorToMove != playingColorString)
          opponentMoveToRepeat = speakString
        saySomething((colorToMove != playingColorString),speakString)
      }

    const currentFen = (moves == "") ? INITIAL_FEN : lastMoveChessJS.after
    animateFen.value = [forceFen.value , currentFen]
    updateCurrentFenAfterDelay(currentFen)
    //forceFen.value = currentFen

       // player's move, get the list of available using ChessJS
    //     if opponent's move, just wait
    
    if (colorToMove == playingColorString) {
      statusDisplay.value = translateEnglishWords("Make Move")
      const moves = chess.moves({ verbose: true })
      availableMoves.value = buildAvailableMoves(moves,false)

      //console.log("available moves",availableMoves.value)
      trackLichessUsage()
      //getAvailableMoves(moves)
    }
    else {
      // if (processingGameFull)
      //   getAvailableMoves(moves)
      trackLichessUsage()
      availableMoves.value = []
      statusDisplay.value = translateEnglishWords("Waiting") + "..."
    }

    // setup and update the timers

    playerSecondsLeft = (playingColorString == "w") ? wtime : btime
    opponentSecondsLeft = (playingColorString == "w") ? btime : wtime
  
    if (wtime == lichessNoTimerValue) {
      if (processingGameFull) {
        opponentTimeDisplay.value = translateEnglishWords("Not Timed")
        playerTimeDisplay.value = translateEnglishWords("Not Timed")
      }
    }
    else {
      // if from "gameFull", set timer if needed
      if (processingGameFull) {
        gameTimerIsCountingDown = false
        startTimer()
      }

      updateTimerViews()

        if (colorToMove == playingColorString) {
            gameTimerAgainstPlayer = true
            timerAmount = Math.floor(playerSecondsLeft / 1000)
        }
        else {
            gameTimerAgainstPlayer = false
            timerAmount = Math.floor(opponentSecondsLeft / 1000)
        }
        gameTimerIsCountingDown = true
    }
    if (status != "started") {
      isDoneWithLichessStream = true
      parseGameOver(status,winner)
    }
  }

 function toplineClick(whatClicked) {
    //console.log("topline",whatClicked)
    switch (whatClicked) {
      case "done" :
        processWords("command done")
        break;
      case "microphone" :
        microphoneIsMuted.value = !microphoneIsMuted.value 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(microphoneIsMuted.value)
        break

    }
  }

  // START Explain Tour Routines

  function explainTourInitialize(pageName,errorText) {
    showState.value = SHOW_EXPLAINTOUR
    explainTourScreen.value = (pageName != null) ? pageName : ("ERROR:" + errorText);
    wordlist.value = connectionStore.getExplainTourWords
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
  }

  function explainTourToolbarClick(whatClicked) {
    switch (whatClicked) {
    case "mute" :
      microphoneIsMuted.value = true 
      emit("mic-is-muted",microphoneIsMuted.value)
      connectionStore.setMicrophoneIsMuted(true)
      break
    case "listen" :
      microphoneIsMuted.value = false 
      emit("mic-is-muted",microphoneIsMuted.value)
      connectionStore.setMicrophoneIsMuted(false)
      break
    case "done" :
      showState.value = SHOW_PLAY
      wordlist.value = wordlistPlay
      emit("word-list",wordlist.value)
      wordlistTrigger.value++
      break
    }
  }

  // END Explain Tour Routines

  function toolbarClick(arrayIndex) {
    //console.log("toolbar click and show state ",arrayIndex,showState.value)
    switch (showState.value) {
      case SHOW_EXPLAINTOUR :
        processWords("command done")
        break
      case SHOW_SINGLE_OPTION :
        if (arrayIndex == TOOLBAR_OPTION_DONE)
          processWords("command done")
        else
          processWords("command explain")
        break
        break
      case SHOW_PLAY :
        //console.log("show play index ",arrayIndex)
        switch (arrayIndex) {
          case TOOLBAR_RESIGN :
            if (opponentIsGone.value && claimWinInSeconds.value == 0)
              processWords("command claim")
            else
              processWords("command resign")
            break
          case TOOLBAR_EXPLAINTOUR :
            processWords("command explain")
            break;
        }
        break
    }
  }

  function singleOptionClick(index) {
    singleOptionSelected.value = singleOptionArray.value[index].radio
  }

  function singleOptionToolbarClick(selected) {
    //console.log("single option toolbar click",selected)
    toolbarTrigger.value++
    switch (selected) {
      case "mute" :
        microphoneIsMuted.value = true 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(true)
        break
      case "listen" :
        microphoneIsMuted.value = false 
        emit("mic-is-muted",microphoneIsMuted.value)
        connectionStore.setMicrophoneIsMuted(false)
        break
      case "explain" :
        explainTourInitialize("options_single",null)
        break
      case "done" :
        //console.log("in done for single" ,singleOptionSelected.value)
        if (singleOptionSelected.value == "1") {
            //console.log("**** END GAME")
            setResign()
        }
        else {
         //console.log("**** NO END GAME")
        }
        switchToPlay()
        break
    }
  }

  function processLichessPlayerResignJson(json) {
    //console.log("**** RESPONSE BACK FROM LICHESS AFTER RESIGN",json)
    gameState = GAME_ENDED
  }

  function setResign() {
    // player is resigning or aborting game, send that to Lichess.org
    const formData = {
    }
    const action = canAbortGame() ? "abort" : "resign"
    const urlStr = "https://lichess.org/api/board/game/" + openGameId + "/" + action
    getLichessData(urlStr, lichessAccessToken,formData, processLichessPlayerResignJson) 
  }

  function processLichessClaimWinJson(json) {
    console.log("**** RESPONSE BACK FROM LICHESS AFTER CLAIM WIN",json)

    if (json.hasOwnProperty('error')) {
      claimResponseError.value = json['error']
      claimWinFailed.value = true
    }
    else {
      claimWinFailed.value = false
      claimResponseError.value = ""
    }
  }

  function claimWin() {
    // player is resigning or aborting game, send that to Lichess.org
    const formData = {
      blah : "blah"
    }
    const action = "claim-victory"
    const urlStr = "https://lichess.org/api/board/game/" + openGameId + "/" + action
    console.log("url for claim",urlStr)
    getLichessData(urlStr, lichessAccessToken,formData, processLichessClaimWinJson) 
  }
  function repeatOpponentMove() {
    //console.log("move to repeat",opponentMoveToRepeat)
    if (opponentMoveToRepeat == "")
      saySomething(false,translatePhrase("no opponent move to repeat"))
    else
      saySomething(false,opponentMoveToRepeat)
  }
  
  function verifyResign() {
    singleOptionHeading.value = "Resign?"
    singleOptionArray.value = []
    singleOptionDisplayWordsArray = []
    singleOptionDisplayWordsArray.push("yes")
    singleOptionDisplayWordsArray.push("no")
    var oneDisplay = { index : 0, name : translateEnglishWords("Yes"), id : 1,radio : "1" , words : "yes"}
    singleOptionArray.value.push(oneDisplay)
    oneDisplay = { index : 1, name : translateEnglishWords("No"), id : 0,radio : "0" , words : "no"}
    singleOptionArray.value.push(oneDisplay)
    singleOptionSelected.value = "0"
    singleOptionServerField = ""
    useSingleOptionDisplayWords = buildWordListFromChoiceList(singleOptionDisplayWordsArray)
    //console.log("****************** single option array",singleOptionArray.value)
    //console.log("****************** single option word list",useSingleOptionDisplayWords)

    wordlist.value = useSingleOptionDisplayWords
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
    toolbarTrigger.value++
    showState.value = SHOW_SINGLE_OPTION
  }

  function processWordsForPlay(text) {
    const returnArray = parseGameWordsSaid(text,gameState != GAME_ENDED,true,availableMoves,playKeyword)
    console.log("command found",returnArray)

    switch (returnArray[0]) {
      case "ambiguous" :
        textareaMessage.value = returnArray[1]
        if (firstAmbiguous) {
          saySomething(true,returnArray[2])
          firstAmbiguous = false
        }
        else
          saySomething(true,returnArray[2])
        break;
      case "play" :
        if (showState.value == SHOW_PLAY) {
          const arrayIndex = returnArray[1]
          forceFen.value = availableMoves.value[arrayIndex].fen
          boardMove(arrayIndex,0,0)
        }
        break
      case "describe" :
        const describeString = processDescribe(returnArray[1],returnArray[2],returnArray[3],returnArray[4], 
                returnArray[5],returnArray[6],fileWords,displayFen)
        saySomething(true,describeString)
        break
      case "command" :
        switch (returnArray[1]) {
          case "claim" :
            // can claim the win if the opponent is gone and there are zero seconds of waiting time
            // AND on the opponent's move
            if (availableMoves.value.length == 0 && gameState != GAME_ENDED && opponentIsGone.value && claimWinInSeconds.value == 0)
              claimWin()
            break
          case "resign" :
          case "abort" :
            if (gameState != GAME_ENDED)
              verifyResign()
            break
          case "repeat" :
            repeatOpponentMove()
            break
          case "explain" :
            explainTourInitialize("verbalplay",null)
            break;
          case "mute" :
            microphoneIsMuted.value = true 
            emit("mic-is-muted",microphoneIsMuted.value)
            connectionStore.setMicrophoneIsMuted(true)
            break
          case "listen" :
            microphoneIsMuted.value = false 
            emit("mic-is-muted",microphoneIsMuted.value)
            connectionStore.setMicrophoneIsMuted(false)
            break
          case "done" :
            isDoneWithLichessStream = true
            emit("where-next",whoSent)
            break;
        }
        break
    }
  }

  function switchToPlay() {
    showState.value = SHOW_PLAY
    wordlist.value = wordlistPlay
    emit("word-list",wordlist.value)
    wordlistTrigger.value++
    toolbarTrigger.value++
  }

  function processWordsForExplain(text) {
    const command = parseGeneralWordsSaid(text,[])
    //console.log("****** command for explain",text,command)
    switch (command[0]) {
      case "done" :
        //console.log("***** DONE WITH EXPLAIN")
        switch (explainForState) {
          case SHOW_PLAY :
            switchToPlay()
            break
          case SHOW_SINGLE_OPTION :
            showState.value = SHOW_SINGLE_OPTION
            break
        }
        break
      case "scroll" :
        scrollCounter++
        explainScrollTrigger.value = (command[1] == "down") ? scrollCounter : -scrollCounter
        break
    }
  }

  function processWords(text) {
    switch (showState.value) {
      case SHOW_EXPLAINTOUR :
        //console.log("*** words for explain tour",text)
        explainTourWordsSpoken.value = text
        explainTourWordsSpokenTrigger.value++
        break
      case SHOW_SINGLE_OPTION :
        singleOptionWordsSpoken.value = text
        singleOptionWordsSpokenTrigger.value++
        break
      case SHOW_PLAY :
        processWordsForPlay(text)
        break
    }
  }


  function boardMove(arrayIndex,from,to) {
    //console.log("board move ",arguments)
    const oneMove = availableMoves.value[arrayIndex]
    //console.log("**** onemove in board move",arrayIndex,oneMove)

    //saySomething(true,oneMove.verbalSan)

    forceFen.value = oneMove.fen
    processValidMove(arrayIndex)
  }

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function saySomething(sayAsPlayer,moveString) {
    emit('string-to-speak',sayAsPlayer,moveString)
  }

  function formatTime(timeInSeconds) {
    var hoursString = ""
    var minutesString = ""
    var secondsString = ""

    const hours = Math.floor(timeInSeconds / 3600)
    const minutesSeconds = Math.floor(timeInSeconds % 3600)
    const minutes = Math.floor(minutesSeconds / 60)
    const seconds = Math.floor(minutesSeconds % 60)

    if (seconds >= 10)
        secondsString = ":" + seconds
    else
        secondsString = ":0" + seconds
    if (hours > 0) {
      hoursString = hours + ":"
      if (minutes >= 10)
        minutesString = minutes
      else
        minutesString = "0" + minutes
    }
    else {
      hoursString = "";
      if (minutes > 0)
        minutesString = minutes
      else
        minutesString = "";
    }
    return hoursString + minutesString + secondsString
  }

  function updateTimerViews() {
    const playerTimeString = formatTime(Math.floor(playerSecondsLeft / 1000))
    let opponentTimeString = formatTime(Math.floor(opponentSecondsLeft / 1000))
    opponentTimeDisplay.value = opponentTimeString
    playerTimeDisplay.value = playerTimeString
  }

  function startTimer() {
    
    function oneSecondInterval() {
      if (timerCanStop || timerAmount < 0) {
        clearInterval(id)
        return
      }

      if (gameTimerIsCountingDown) {
        timerAmount--
        const timeString = formatTime(timerAmount)
        if (gameTimerAgainstPlayer)
          playerTimeDisplay.value = timeString
        else
          opponentTimeDisplay.value = timeString
      }
    }
    var id = setInterval(oneSecondInterval, 1000)
  }

  function processAvailableMovesResponse(json) {
    
    //console.log("**** available move json",json)
    if (json.hasOwnProperty('error')) {
      explainTourInitialize(null,generateErrorAlertText(json))
      return;
    }

    playKeyword = json["play_keyword"]
    moveNumber = Math.floor(json["move_number"])

    displayFen = json["display_fen"]
    pgn = json["pgn"]
    pgnLast10 = json["pgn_last10"]
   
    var firstMoveNote = ""
    if (moveNumber <= 1)
      firstMoveNote = buildfirstMoveNote()

    if (processingGameFull) {
      forceFen.value = displayFen
      textareaMessage.value = pgnLast10 + "<br>" + firstMoveNote
    }


    opponentMoveCount = Math.floor(json["eng_count"])
    opponentFen = json["eng_fens"]
    opponentSan = json["eng_sans"]
    opponentVerbalSan = json["eng_verbal_sans"]

    playerMoveCount = Math.floor(json["play_count"])

    const playerFens = json["play_fens"].split(",")
    const playerFroms = json["play_froms"].split(",")
    const playerTos = json["play_tos"].split(",")
    playerSans = json["play_sans"].split(",")
    const playerVerbalSans = json["play_verbal_sans"].split("\t")
    const playerPromotions = json["play_promotion"].split(",")

    availableMoves.value = []
    for (var i=0; i < playerMoveCount; i++) {
      const oneMove = { fen : playerFens[i],
                          from : playerFroms[i],
                          to : playerTos[i],
                          san : playerSans[i],
                          verbalSan : playerVerbalSans[i],
                          promotion : playerPromotions[i] }
      availableMoves.value.push(oneMove)
    }

    if (opponentMoveCount > 0)
      processOpponentMove()
  }

  onBeforeUnmount(() => {
    //console.log("**************** Lichess Game unmounted")
    stopWaiting = true
    timerCanStop = true
    
    isDoneWithLichessStream = true

  })

  function canAbortGame() {
    return (moveStackChessJS.length <= 2)
  }

  function buildfirstMoveNote() {
    var firstMoveNote1 = ""
    var firstMoveNote2 = ""
    switch (playKeyword) {
    case "no" :
        firstMoveNote1 = translatePhrase("No Play Keyword Required")
        firstMoveNote2 = translatePhrase("Just say e4 or echo 4")
        break;
    case "piece" :
        firstMoveNote1 = translatePhrase("Say piece name even if pawn")
        firstMoveNote2 = translatePhrase("Pawn e4 or Pawn echo 4")
        break;
    default :
        firstMoveNote1 = translatePhrase("Play Keyword Required")
        firstMoveNote2 = translatePhrase("Say 'play e4' or 'play echo 4'")
        break;
    }
    const firstMoveNote3 = translateEnglishWords("Piece Names : rook knight bishop queen king pawn")
    return ("<br>" + firstMoveNote1 + "<br>" + firstMoveNote2 + "<br>" + firstMoveNote3)
  }  
  
  // opponent made a move. display it, say it and animate it
  function processOpponentMove() {
     
    //console.log("IN PROCESS OPPONENT")
    // switch timer to player and update values with latest from lichess.org
    if (playerSecondsLeft != lichessNoTimerValue) {
        gameTimerIsCountingDown = false
        updateTimerViews()
        gameTimerAgainstPlayer = true
        timerAmount = Math.floor(playerSecondsLeft / 1000)
        gameTimerIsCountingDown = true
    }
          
    // button text
    
    toolbarElements.value[TOOLBAR_RESIGN].label = canAbortGame() ? translateEnglishWords("Abort") 
                          : translateEnglishWords("Resign")
    toolbarTrigger.value++

    statusDisplay.value = translateEnglishWords("Make Move")
    var firstMoveNote = ""

    if (moveNumber <= 1) {
      firstMoveNote = buildfirstMoveNote()
    }

    textareaMessage.value = pgnLast10 + "<br>" + firstMoveNote
    opponentMoveToRepeat = opponentVerbalSan
    animateFen.value = [forceFen.value , opponentFen]
    //console.log("************************************ here before SAY SOMETHIG OPPONENT")
    saySomething(false,opponentVerbalSan)
    //console.log("END PROCESS OPPONENT")
  }

  function getAvailableMoves(moveString) {

    var formData = {
      moves : moveString,
      color : playingColorString,
      words : fileWords,
      keyword : playKeyword
    }

    //console.log("getAvailableMoves formdata",formData)
    useGetData("getweb4_verbal_lichess_process_moves.php", formData, processAvailableMovesResponse)
  }

   function processJsonResponse (json) {
  }

  // track usage since everything is done in 
  function trackLichessUsage() {
    var formData = {
    }

    //console.log("Set track of lichess usage ",formData)
    useGetData("getweb5_verbal_lichess_process_moves.php", formData,processJsonResponse)
  }

  function processLichessPlayerMoveJson(json) {
    //console.log("**** RESPONSE BACK FROM LICHESS AFTER MOVE",json)

    // WORK - check for error
    if (json.hasOwnProperty('error')) {
      //console.log("here")
      const errorText = "ERROR while sending move to Lichess -> " + urlSentToLichess + "\n" +
                  "Length " + urlSentToLichess.length + "\n" +
                  "*** You need to back out to the Lichess Menu and bring up 'Open Games List'\n\n" +
                  "*** PLEASE send this message to henry@chessvis.com"
      explainTourInitialize(null,errorText)
      return;
    }

    // let the stream processing handle time or game status updates

  }

  // player made a valid move from the list of valid moves. Send it to Lichess.org
  function processValidMove(index) {
 
    const fileLetters = [ "a", "b", "c", "d", "e", "f", "g", "h"]
    const oneMove = availableMoves.value[index]
    const fenString = oneMove.fen
    const from = oneMove.from
    const to = oneMove.to
    const promotion = oneMove.promotion
    const san = oneMove.san
    
    const moveDots = (playingColorString == "w") ? ". " : "... "
        
    textareaMessage.value = moveNumber + moveDots + san
    availableMoves.value = []    
    
    opponentMoveToRepeat = ""

    const fromRank = 8 - Math.floor(from/8)
    const fromFile = fileLetters[from % 8]
    const toRank = 8 - Math.floor(to/8)
    const toFile = fileLetters[to % 8]
    
    var uci = fromFile + fromRank + toFile + toRank
    if (promotion != "" && promotion != " ")
      uci += promotion

    const formData = {
    }
    urlSentToLichess = "https://lichess.org/api/board/game/" + openGameId + "/move/" + uci
    //console.log("send move to lichess    ",urlSentToLichess)
    //console.log("   uci sent & length -> ",uci,uci.length)
    getLichessData(urlSentToLichess, lichessAccessToken,formData, processLichessPlayerMoveJson) 
}

</script>

<style scoped>

.name-text {
  display: inline-block;
  width : 48%;
  margin-left : 2%;
  position : relative;
  text-align : left;
}

.time-text {
  display: inline-block;
  width : 48%;
  margin-right : 2%;
  position : relative;
  text-align : right;
}

.blurred-text {
  filter: blur(3px); /* Adjust the pixel value for desired blur intensity */
}

</style>